/**
 * The free Lp(a) test with a new Swiss membership (AHD-14738): when it
 * ends, how the copy says that day, and its kill switch. Shared with the
 * booking flow: the same end date (LPA_GIFT_ENDS in its lpaGift.ts) and the
 * same `lpa_gift` row in the booking's Supabase `feature_flags` table.
 */
import { SUPABASE_ANON_KEY, SUPABASE_URL } from "@/lib/pricing"

/** The offer's end (weekly 03.11: "temporarily free", the date in the
 *  small print): from then on the gift shows nowhere. */
export const LPA_GIFT_ENDS = Date.parse("2027-01-01T00:00:00+01:00")

export const lpaGiftRunning = (at: number = Date.now()) => at < LPA_GIFT_ENDS

/** The offer's last day in the page's locale, for the copy's {date}:
 *  "31 December 2026", "31. Dezember 2026", "31 décembre 2026". */
export function lpaGiftLastDay(locale: string): string {
  const lang = locale.slice(0, 2)
  return new Intl.DateTimeFormat(lang === "en" ? "en-GB" : `${lang}-CH`, {
    timeZone: "Europe/Zurich",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(LPA_GIFT_ENDS - 1)
}

/**
 * The `lpa_gift` flag, read on the server: on unless its row says off, and
 * on when the table can't be read, so a Supabase blip never hides the
 * offer. Cached a minute, so switching it off lands within one.
 */
export async function fetchLpaGiftFlag(): Promise<boolean> {
  try {
    const res = await fetch(
      `${SUPABASE_URL}/rest/v1/feature_flags?key=eq.lpa_gift&select=enabled`,
      {
        headers: {
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        },
        signal: AbortSignal.timeout(3000),
        next: { revalidate: 60, tags: ["feature-flags"] },
      }
    )
    if (!res.ok) return true
    const rows = (await res.json()) as { enabled?: boolean }[]
    return rows[0]?.enabled ?? true
  } catch {
    return true
  }
}
